import type { Permissions } from '@/apis/auth/type'
import { USER_PERMISSION_KEY, USER_PERMISSION_TIP } from '@/data/auth'
import { translateLang } from '@/data/i18n/translateLang'
import store from '@/store'
import { message } from 'ant-design-vue'
import Vue from 'vue'

type PermissionKey = keyof Permissions
type PermissionCheckResult = true | PermissionKey[]

function formatPermissionKeys(
  permissionKeys: PermissionKey | PermissionKey[]
): PermissionKey[] {
  return Array.isArray(permissionKeys) ? permissionKeys : [permissionKeys]
}

export function checkAndShowNoPermissionToast(
  permissionKeys: PermissionKey[],
  tip = '您暂无此功能的使用权限，请购买或续费后再进行操作。'
): boolean {
  const checkResult = checkUserPermissions(permissionKeys)
  if (checkResult === true) {
    return true
  }

  const translate = Vue.prototype.$translateLang
  Vue.prototype.$confirm(translate(tip), translate('提示'), {
    confirmButtonText: translate('确定'),
    cancelButtonText: translate('取消'),
    type: 'warning',
  })

  return false
}

/**
 * @description 统一判断用户功能权限，避免各业务组件重复读取 store 权限结构
 * @returns true 表示全部支持；数组表示当前不支持的权限字段；空权限数组视为全部支持
 */
export function checkUserPermissions(
  permissionKeys: PermissionKey[]
): PermissionCheckResult {
  if (!permissionKeys.length) {
    return true
  }
  const unsupportedPermissionKeys = permissionKeys.filter((permissionKey) => {
    return !store.state.userPermissions?.[permissionKey]
  })

  return unsupportedPermissionKeys.length ? unsupportedPermissionKeys : true
}

/**
 * @description 判断用户功能权限，不满足时直接 toast 提示业务文案
 * @returns true 表示拥有权限；false 表示缺少权限且已经提示
 */
export function checkUserPermissionWithToast(
  permissionKeys: PermissionKey | PermissionKey[],
  tip: string
): boolean {
  const checkResult = checkUserPermissions(formatPermissionKeys(permissionKeys))
  if (checkResult === true) {
    return true
  }

  message.warning(tip)
  return false
}

// 构建 permission key value → 中文文案 的反查表
const PERMISSION_TIP_MAP: Record<string, string> = {}
;(
  Object.keys(USER_PERMISSION_KEY) as (keyof typeof USER_PERMISSION_KEY)[]
).forEach((name) => {
  PERMISSION_TIP_MAP[USER_PERMISSION_KEY[name]] = USER_PERMISSION_TIP[name]
})

/**
 * @description 检查多个权限，缺少时统一提示具体缺少哪些权限（多语言）
 * @returns true 全部通过；false 有缺失（已提示）
 */
export function checkUserPermissionsWithDetailToast(
  permissionKeys: PermissionKey | PermissionKey[]
): boolean {
  const checkResult = checkUserPermissions(formatPermissionKeys(permissionKeys))
  if (checkResult === true) return true

  const separator = translateLang('、' as any)
  const missingTips = checkResult.map((key) => {
    const tipText = PERMISSION_TIP_MAP[key] ?? key
    return translateLang(tipText as any)
  })
  message.error(
    translateLang('缺少以下权限：{permissions}' as any, {
      permissions: missingTips.join(separator),
    })
  )
  return false
}
